import {
    useCallback, useEffect, useMemo, useState,
} from 'react'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import {
    UseMutationResult,
} from '@tanstack/react-query'
import useFieldsWithClassNames from 'app/hooks/useFieldsWithClassName'

import useValidation from 'app/hooks/useValidation'
import SkyNetStepperButtons from 'app/shared-components/SkyNetStepper/SkyNetStepperButtons'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import useMarginStyles from 'app/tss/margin.styles'
import {
    Currency, LaneFeesStatus, LaneQuoteViewType, LaneStatus,
} from 'app/enums/label.enums'
import useRequest, {
    RequestFn, RequestKeys,
} from 'app/hooks/useRequest'
import requests from 'app/requests/CQuote.requests'

import Loading from 'app/shared-components/Loading'
import useEnumValues from 'app/hooks/useEnumValues'
import WarningCard from 'app/shared-components/WarningCard'
import {
    WarningCardVariant,
} from 'app/enums/common.enums'

import useCQuoteInformationContext, {
    CQuoteContactDetails as TCQuoteContactDetails,
    TCQuoteStepper,
} from '../../CQuoteInformationContext'
import useGetCQuotesFeesData from '../../hooks/useGetCQuoteFeesData'
import AdditionalFeesTable from '../../../AdditionalFeesTable'
import LaneFeesTable from '../../../LaneFeesTable'
import FeesOverview from '../../../FeesOverview'
import getFields from './CQuoteFeesContactDetails.fields'
import useStyles from './CQuoteFeesContactDetails.styles'

export default function CQuoteFeesContactDetails({
    onCreate,
    onGeneratePDF,
}: Readonly<{
    onCreate: UseMutationResult,
    onGeneratePDF: UseMutationResult,
    index: number
}>) {
    const getAllowedValues = useEnumValues()
    const {
        classes: marginClasses,
    } = useMarginStyles({
        top: 3,
    })
    const {
        classes,
    } = useStyles()

    const {
        value: contextValue,
        setValue: setContextValue,
        setActiveStep,
        activeStep,
    } = useCQuoteInformationContext()

    const [
        detailsApplied,
        setDetailsApplied,
    ] = useState<boolean>(false)

    const [
        value,
        setValue,
    ] = useState<TCQuoteStepper>({
        ...contextValue,
        contactDetails: {
            generatePdf: true,
            laneQuoteViewType: LaneQuoteViewType.DETAILED_VIEW,
        } as TCQuoteContactDetails,
    } as TCQuoteStepper)

    const requestData = useMemo(() => {
        const result = {
            expPalletsYear: value.laneDetails?.expPalletsYear,
            rentalDays: value?.offerSetup?.rentalDays,
            logisticsDays: value?.offerSetup?.logisticsDays,
            isPerformanceQualification: value?.offerSetup?.isPerformanceQualification,
        }

        switch (value?.laneDetails?.laneStatus) {
        case LaneStatus.TO_BE_QUOTED:
        case LaneStatus.UNDER_REVIEW_PRICING:
            return {
                ...result,
                lane: value?.lane,
                pricingAccount: value.laneDetails?.contractBasisAccount,
                handoverPoint: value.deliveryOptions?.handoverPoint
                        || value.laneDetails?.handoverPoint,
                collectionDropoffPoint: value.deliveryOptions?.collectionDropoffPoint
                        || value.laneDetails?.collectionDropoffPoint,
                currency: value?.offerSetup?.currency,
                customerQuotation: value?.offerSetup?.customerQuotation,

            }
        case LaneStatus.IN_PROGRESS:
        case LaneStatus.PRICED:
        case LaneStatus.AWARDED:
        case LaneStatus.IMPLEMENTED:
        case LaneStatus.INACTIVE:
            return {
                ...result,
                lane: value?.lane,
            }
        default:
            return {
                ...result,
                pricingAccount: value.laneDetails?.contractBasisAccount,
                tempRange: value?.laneDetails?.temperatureRange,
                productType: value?.laneDetails?.productType,
                originAirport: value.laneDetails?.originAirport,
                destinationAirport: value.laneDetails?.destinationAirport,
                deliveryServiceType: value.deliveryOptions?.deliveryServiceType
                        || value.laneDetails?.deliveryServiceType,
                collectionServiceType: value.deliveryOptions?.collectionServiceType
                        || value.laneDetails?.collectionServiceType,
                handoverPoint: value.deliveryOptions?.handoverPoint
                        || value.laneDetails?.handoverPoint,
                collectionDropoffPoint: value.deliveryOptions?.collectionDropoffPoint
                        || value.laneDetails?.collectionDropoffPoint,
                currency: value?.offerSetup?.currency,
                customerQuotation: value?.offerSetup?.customerQuotation,
            }
        }
    }, [value])

    const {
        data,
        isDataReady,
        isFetching,
        error,
    } = useGetCQuotesFeesData(requestData)

    const {
        data: contactDetails,
        isDataReady: contactDetailsDataReady,
    } = useRequest<TCQuoteContactDetails>({
        key: RequestKeys.getCQuoteQuotationDetails,
        params: {
            lane: value?.lane,
        },
        requestFunc: RequestFn.getEntity(requests.getCQuoteQuotationDetails),
        enabled: Boolean(value?.lane?.id),
    })

    const laneFeesSubmitted = useMemo(() => {
        return data?.laneFees?.laneFeesStatus === LaneFeesStatus.SUBMITTED
    }, [data])

    const onChange = useCallback((newValue: TCQuoteContactDetails) => {
        if (!isEqual(newValue, value?.contactDetails)) {
            setValue({
                ...value,
                contactDetails: newValue,
            })
        }
    }, [value])

    const nextAction = useCallback(() => {
        const payload = {
            ...requestData,
            ...value.contactDetails,
        }

        if (laneFeesSubmitted) {
            onGeneratePDF.mutate({
                ...payload,
                locationName: value?.offerSetup?.customerQuotation.locationName,
            })
        } else {
            onCreate.mutate(payload)
        }
    }, [
        laneFeesSubmitted,
        onCreate,
        onGeneratePDF,
        requestData,
        value.contactDetails,
        value?.offerSetup?.customerQuotation,
    ])

    const {
        isValid,
    } = useValidation({
        fields: [],
        values: value?.offerSetup || {},
    })

    const backAction = useCallback(() => {
        setActiveStep(activeStep - 1)
        setContextValue({
            ...value,
            contactDetails: {} as TCQuoteContactDetails,
        })
    }, [
        setActiveStep,
        activeStep,
        setContextValue,
        value,
    ])

    useEffect(() => {
        if (contactDetailsDataReady
            && !detailsApplied && !isEqual(contactDetails, pick(value?.contactDetails, [
            'skyCellContact',
            'quotedFor',
        ]))) {
            setValue({
                ...value,
                contactDetails: {
                    ...contactDetails,
                    ...value.contactDetails,
                },
            })
            setDetailsApplied(true)
        }
    }, [
        contactDetailsDataReady,
        contactDetails,
        value,
        detailsApplied,
    ])

    useEffect(() => {
        if (error) {
            setActiveStep((prev) => {
                return prev - 1
            })
        }
    }, [
        error,
        setActiveStep,
    ])

    const fields = useMemo(() => {
        return getFields(getAllowedValues, laneFeesSubmitted)
    }, [
        getAllowedValues,
        laneFeesSubmitted,
    ])

    const fieldsWithClassNames = useFieldsWithClassNames(fields, classes)

    if (isFetching) return <Loading />
    if (!isDataReady) return null

    return (
        <>
            {laneFeesSubmitted && (
                <WarningCard variant={WarningCardVariant.Warning}>
                    <div className={classes.warningContent}>
                        <div>
                            <span className={classes.warningTitle}>IMPORTANT NOTE: </span>
                            The fees have already been submitted.
                            For new fees, please contact the pricing team.
                        </div>
                        <div>
                            If you require a quote document based on the current fees,
                            please proceed to generate a Quote
                            PDF.
                        </div>
                    </div>

                </WarningCard>
            )}
            <FeesOverview value={data} />
            <LaneFeesTable
                value={data?.laneFees?.fees}
                currency={value.offerSetup?.currency as unknown as Currency}
                title="Lane Fees"
            />
            <AdditionalFeesTable
                value={data?.additionalFees.fees}
                currency={value.offerSetup?.currency as unknown as Currency}
                title="Additional Fees"
            />
            <DomainObjectForm
                value={value?.contactDetails}
                onChange={onChange}
                fields={fieldsWithClassNames}
                name="FeesAndContactDetails"
                className={marginClasses.margin}
            />
            <SkyNetStepperButtons
                isValid={isValid}
                nextLabel={laneFeesSubmitted ? 'Generate' : 'Create'}
                nextAction={nextAction}
                canGoBack
                backAction={backAction}
                isLoading={onCreate.isPending}
            />
        </>
    )
}
